import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ChevronLeft, 
  ChevronRight, 
  ChevronsLeft, 
  ChevronsRight,
  Search,
  Filter,
  RefreshCcw,
  Calendar,
  Clock,
  Activity,
  UserCheck,
  Video,
  Plus,
  ArrowUpRight,
  LayoutGrid,
  List,
  CheckCircle2,
  Clock3,
  XCircle,
  AlertCircle,
  AlertTriangle
} from "lucide-react";
import TeleconsultCard from "../../components/Cards/TeleconsultCard";
import { ConsultWithParticipants } from "../../components/interface/consult";
import { BASE_URL } from "../../components/interface/Config";
import { apiHandler } from "../../services/authService";
import { fetchUserInfo } from "../../services/providerService";
import { AmbientAssistant } from "../../components/AmbientAI/AmbientAssistant";
import { CreateConsultModal } from "../../components/Modals/CreateConsultModal";

const SafeAnimatePresence = AnimatePresence as any;

const Teleconsult: React.FC = () => {
  const [history, setHistory] = useState<ConsultWithParticipants[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(12);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [userName, setUserName] = useState("Doctor");
  const [activeStatus, setActiveStatus] = useState<string>("");
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);

  const token = sessionStorage.getItem("authToken");

  useEffect(() => {
    const getUser = async () => {
      try {
        const info = await fetchUserInfo();
        if (info?.data?.first_name) setUserName(info.data.first_name);
      } catch (e) { console.error(e); }
    };
    getUser();
  }, []);

  const fetchHistory = async (page: number, limit: number) => {
    setLoading(true);
    try {
      const url = `${BASE_URL}/resource/consults`;
      const payload = {
        page: page + 1,
        limit: limit,
        searchkey: "",
        patient_name: searchQuery,
        provider_name: "",
        order_by: "id",
        dir: 2,
        from_date: "",
        to_date: "",
        participant_ref_number: "",
        consult_status: activeStatus,
        consult_id: "",
      };

      if (!token) throw new Error("No auth token found");
      const response = await apiHandler(token, 'GET', payload, url);

      if (response && response.data) {
        const data = response.data.data;
        const fetchedHistory: ConsultWithParticipants[] = data.consults.map((historyItem: any) => {
          const participants = historyItem.participants || [];
          const publisher = participants.filter((participant: any) => participant.role === "publisher");
          const subscriber = participants.filter((participant: any) => participant.role === "subscriber");

          return {
            ...historyItem,
            participants: { publisher, subscriber },
          };
        });

        setHistory(fetchedHistory);
        setTotalRecords(data.pagination.total || 0);
      }
    } catch (error) {
      console.error("Error fetching teleconsult history:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchHistory(currentPage, rowsPerPage);
  }, [currentPage, rowsPerPage, token, searchQuery, activeStatus]);

  const handlePageChange = (newPage: number): void => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const totalPages = Math.ceil(totalRecords / rowsPerPage);
  const now = new Date();
  const greeting = now.getHours() < 12 ? "Good Morning" : now.getHours() < 17 ? "Good Afternoon" : "Good Evening";

  const statusFilters = [
    { label: "All", value: "", icon: <Activity size={14} /> },
    { label: "New", value: "New", icon: <AlertCircle size={14} /> },
    { label: "Waiting", value: "Waiting", icon: <Clock3 size={14} /> },
    { label: "Started", value: "Started", icon: <Video size={14} /> },
    { label: "Ended", value: "Ended", icon: <CheckCircle2 size={14} /> },
    { label: "Cancelled", value: "Cancelled", icon: <XCircle size={14} /> },
  ];

  return (
    <div className="space-y-6 md:space-y-10 pb-12">
      {/* Premium Hero Section */}
      <section className="relative overflow-hidden rounded-[2rem] md:rounded-[3rem] bg-slate-900 p-6 md:p-12 text-white shadow-2xl mx-1 md:mx-0">
         <div className="absolute top-0 right-0 w-48 md:w-64 h-48 md:h-64 bg-primary/20 rounded-full blur-[60px] md:blur-[80px] -mr-24 -mt-24 md:-mr-32 md:-mt-32" />
         <div className="absolute bottom-0 left-0 w-32 md:w-48 h-32 md:h-48 bg-indigo-500/10 rounded-full blur-[40px] md:blur-[60px] -ml-16 -mb-16 md:-ml-24 md:-mb-24" />
         
         <div className="relative z-10 flex flex-col lg:flex-row lg:items-end justify-between gap-6 md:gap-8">
            <div className="space-y-3 md:space-y-4">
               <motion.div 
                 initial={{ opacity: 0, x: -20 }}
                 animate={{ opacity: 1, x: 0 }}
                 className="inline-flex items-center gap-2 px-3 py-1 md:px-4 md:py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/10 text-[8px] md:text-xs font-black uppercase tracking-[0.2em]"
               >
                 <div className="w-1.5 md:w-2 h-1.5 md:h-2 rounded-full bg-emerald-400 animate-pulse" />
                 Clinical Live
               </motion.div>
               
               <motion.h1 
                 initial={{ opacity: 0, y: 20 }}
                 animate={{ opacity: 1, y: 0 }}
                 className="text-3xl md:text-5xl font-black tracking-tight leading-tight"
               >
                 {greeting}, <span className="text-primary">Dr. {userName}</span>
               </motion.h1>
               
               <p className="text-slate-400 font-medium max-w-md text-base md:text-lg">
                 You have <span className="text-white font-black">{totalRecords} consultations</span> in your queue.
               </p>
            </div>

            <div className="flex items-center gap-4">
               <button 
                 onClick={() => setShowCreateModal(true)}
                 className="flex items-center justify-center gap-3 w-full lg:w-auto px-6 md:px-8 py-3 md:py-4 bg-primary text-white font-black rounded-2xl md:rounded-3xl shadow-xl shadow-primary/25 hover:scale-105 active:scale-95 transition-all text-sm md:text-base cursor-pointer"
               >
                  <Plus className="w-4.5 h-4.5 md:w-5 md:h-5" />
                  New Consultation
               </button>
            </div>
         </div>
      </section>

      {/* Modern Status Filters - Scrollable on mobile */}
      <section className="overflow-x-auto scrollbar-hide -mx-4 px-4 pb-2">
        <div className="flex items-center gap-2 md:gap-3 min-w-max">
           {statusFilters.map((filter) => (
             <button
               key={filter.label}
               onClick={() => {
                  setActiveStatus(filter.value);
                  setCurrentPage(0);
               }}
               className={`flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 rounded-xl md:rounded-2xl text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all duration-300 border ${
                 activeStatus === filter.value 
                   ? "bg-slate-900 text-white border-slate-900 shadow-lg shadow-slate-900/10 scale-105" 
                   : "bg-white text-slate-500 border-slate-100 hover:border-primary/20 hover:bg-slate-50"
               }`}
             >
                <span className={activeStatus === filter.value ? "text-primary" : ""}>
                  {filter.icon}
                </span>
                {filter.label}
             </button>
           ))}
        </div>
      </section>

      {/* Toolbar & Search */}
      <section className="space-y-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 px-1 md:px-4">
           {/* View Toggle - Hidden on very small screens or made compact */}
           <div className="hidden sm:flex items-center gap-2 p-1 bg-slate-100 rounded-xl md:rounded-2xl border border-slate-200">
              <button 
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg md:rounded-xl transition-all ${viewMode === 'grid' ? 'bg-white shadow-sm text-primary' : 'text-slate-400 hover:text-slate-600'}`}
              >
                <LayoutGrid className="w-4 h-4 md:w-[18px] md:h-[18px]" />
              </button>
              <button 
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg md:rounded-xl transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-primary' : 'text-slate-400 hover:text-slate-600'}`}
              >
                <List className="w-4 h-4 md:w-[18px] md:h-[18px]" />
              </button>
           </div>

           <div className="flex items-center gap-2 md:gap-3 w-full md:w-auto">
              <div className="relative flex-grow md:flex-grow-0">
                <Search className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 md:w-[18px] md:h-[18px]" />
                <input 
                  type="text" 
                  placeholder="Patient Name..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 md:pl-12 pr-4 py-2.5 md:py-3 bg-white border border-slate-200 rounded-xl md:rounded-[1.5rem] text-sm focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all w-full md:w-64 lg:w-80 shadow-sm font-medium"
                />
              </div>
              <button 
                onClick={() => fetchHistory(currentPage, rowsPerPage)}
                className="p-2.5 md:p-3 bg-white border border-slate-200 rounded-xl md:rounded-[1.5rem] hover:bg-slate-50 transition-all shadow-sm group shrink-0"
              >
                <RefreshCcw className={`text-slate-600 group-hover:text-primary transition-colors w-4.5 h-4.5 md:w-5 md:h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
           </div>
        </div>

        {/* Content View */}
        <div className="min-h-[400px]">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
               {[...Array(6)].map((_, i) => (
                 <div key={i} className="h-64 bg-white rounded-[2rem] md:rounded-[2.5rem] border border-slate-100 animate-pulse flex flex-col p-6 space-y-4">
                    <div className="flex items-center gap-4">
                       <div className="w-12 h-12 md:w-14 md:h-14 bg-slate-100 rounded-xl md:rounded-2xl" />
                       <div className="space-y-2 flex-grow">
                          <div className="h-4 bg-slate-100 rounded-full w-2/3" />
                          <div className="h-3 bg-slate-100 rounded-full w-1/3" />
                       </div>
                    </div>
                    <div className="h-20 bg-slate-50 rounded-2xl w-full" />
                    <div className="h-10 bg-slate-100 rounded-2xl w-full" />
                 </div>
               ))}
            </div>
          ) : history.length > 0 ? (
            <motion.div 
              layout
              className={viewMode === 'grid' 
                ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8" 
                : "space-y-4"
              }
            >
              <SafeAnimatePresence>
                {history.map((historyItem) => (
                  <TeleconsultCard
                    key={historyItem.id}
                    teleconsultHistory={historyItem}
                    participantName={historyItem.participants.subscriber[0]?.participant_info?.name || "Patient"}
                  />
                ))}
              </SafeAnimatePresence>
            </motion.div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 md:py-32 bg-white rounded-[2rem] md:rounded-[3rem] border border-dashed border-slate-200 mx-1 md:mx-0">
              <div className="w-16 h-16 md:w-24 md:h-24 bg-slate-50 rounded-full flex items-center justify-center mb-4 md:mb-6">
                <Search className="text-slate-200 w-8 h-8 md:w-10 md:h-10" />
              </div>
              <h3 className="text-xl md:text-2xl font-black text-slate-900 mb-1 md:mb-2 text-center">No records found</h3>
              <p className="text-slate-400 font-medium italic text-sm md:text-base text-center px-6">We couldn't find any consultations matching your current filters.</p>
              <button 
                onClick={() => {
                   setSearchQuery("");
                   setActiveStatus("");
                }}
                className="mt-6 md:mt-8 px-6 md:px-8 py-2.5 md:py-3 bg-slate-100 hover:bg-slate-200 rounded-xl md:rounded-2xl text-slate-700 font-bold transition-all text-sm"
              >
                Reset All Filters
              </button>
            </div>
          )}
        </div>

        {/* Pagination Section */}
        {history.length > 0 && (
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 bg-slate-900 p-4 md:p-6 rounded-[2rem] md:rounded-[2.5rem] shadow-2xl mt-8 md:mt-12 text-white">
            <div className="flex items-center gap-3 md:gap-4 w-full lg:w-auto">
              <div className="p-2.5 md:p-3 bg-white/10 rounded-xl md:rounded-2xl shrink-0">
                 <LayoutGrid className="text-primary w-4.5 h-4.5 md:w-5 md:h-5" />
              </div>
              <div className="overflow-hidden">
                 <p className="text-[8px] md:text-[10px] font-black text-white/40 uppercase tracking-widest truncate">Records per page</p>
                 <select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  className="bg-transparent border-none p-0 text-base md:text-lg font-black focus:ring-0 cursor-pointer w-full"
                >
                  {[12, 24, 36, 48].map(val => <option key={val} value={val} className="text-slate-900">{val} per page</option>)}
                </select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 md:gap-8 w-full lg:w-auto">
              <div className="text-center sm:text-right hidden sm:block">
                 <p className="text-[8px] md:text-[10px] font-black text-white/40 uppercase tracking-widest mb-0.5">Current Progress</p>
                 <p className="text-xs md:text-sm font-bold">
                   Page <span className="text-primary">{currentPage + 1}</span> of {totalPages}
                 </p>
              </div>
              
              <div className="flex items-center justify-center gap-1.5 md:gap-2 w-full sm:w-auto">
                <button
                  onClick={() => handlePageChange(0)}
                  disabled={currentPage === 0}
                  className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl md:rounded-2xl bg-white/10 hover:bg-white/20 disabled:opacity-10 transition-all shrink-0"
                >
                  <ChevronsLeft className="w-4.5 h-4.5 md:w-5 md:h-5" />
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                  className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl md:rounded-2xl bg-white/10 hover:bg-white/20 disabled:opacity-10 transition-all shrink-0"
                >
                  <ChevronLeft className="w-4.5 h-4.5 md:w-5 md:h-5" />
                </button>
                
                <div className="px-4 font-black text-xs sm:hidden">
                  {currentPage + 1} / {totalPages}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages - 1}
                  className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl md:rounded-2xl bg-primary shadow-lg shadow-primary/20 hover:scale-105 disabled:opacity-10 transition-all shrink-0"
                >
                  <ChevronRight className="w-4.5 h-4.5 md:w-5 md:h-5" />
                </button>
                <button
                  onClick={() => handlePageChange(totalPages - 1)}
                  disabled={currentPage >= totalPages - 1}
                  className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl md:rounded-2xl bg-white/10 hover:bg-white/20 disabled:opacity-10 transition-all shrink-0"
                >
                  <ChevronsRight className="w-4.5 h-4.5 md:w-5 md:h-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Globally Accessible Ambient AI Assistant Floating Card */}
      <AmbientAssistant />

      {/* Create Consultation Modal */}
      <SafeAnimatePresence>
        {showCreateModal && (
          <CreateConsultModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              if (token) fetchHistory(currentPage, rowsPerPage);
            }}
          />
        )}
      </SafeAnimatePresence>
    </div>
  );
};

export default Teleconsult;
